import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Eye, Copy } from "lucide-react";
import { Panel, Chip, Th, Td, Kpi } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AuditTab } from "@/components/settings/AccountTabs";
import { api, errMsg, ldnTime, fmt } from "@/lib/api";

const STATUS = { active: "OK", pending: "WARNING", suspended: "TRIGGERED" };

function NewUser({ onDone }) {
  const [f, setF] = useState({ email: "", name: "", password: "", view_only: false });
  const submit = async () => {
    try { await api.post("/super/users", f); toast.success("Account created"); setF({ email: "", name: "", password: "", view_only: false }); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Panel title="Create account" testid="super-create-panel">
      <div className="grid gap-2 md:grid-cols-5">
        <Input data-testid="super-new-name" placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="bg-slate-950" />
        <Input data-testid="super-new-email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="bg-slate-950" />
        <Input data-testid="super-new-password" type="password" placeholder="Temporary password (8+)" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="bg-slate-950" />
        <label className="flex items-center gap-2 text-sm text-slate-300"><input data-testid="super-new-view-only" type="checkbox" checked={f.view_only} onChange={(e) => setF({ ...f, view_only: e.target.checked })} /> Investor view only (sees your dashboard)</label>
        <Button data-testid="super-new-submit" className="btn-gold" onClick={submit}>Create account</Button>
      </div>
    </Panel>
  );
}

function Field({ label, value, testid }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-md border border-white/[0.06] bg-slate-950/60 px-3 py-2">
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">{label}</div>
        <div data-testid={testid} className="num break-all text-sm text-slate-100">{value || "-"}</div>
      </div>
      {value && <button data-testid={`${testid}-copy`} onClick={() => { navigator.clipboard.writeText(String(value)); toast.success(`${label} copied`); }} className="text-slate-400 hover:text-[#EBD7A0]"><Copy className="h-4 w-4" /></button>}
    </div>
  );
}

function RevealDialog({ data, onClose }) {
  return (
    <Dialog open={!!data} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="super-reveal-dialog" className="max-w-lg border-white/10 bg-[#0B0F17]">
        <DialogHeader>
          <DialogTitle className="text-slate-100">MT5 details for {data?.email}</DialogTitle>
          <DialogDescription>This view has been written to the audit log. Do not share screenshots of it.</DialogDescription>
        </DialogHeader>
        {data && (
          <div className="space-y-2">
            <Field label="MT5 login" value={data.login} testid="reveal-login" />
            <Field label="MT5 password" value={data.password} testid="reveal-password" />
            <Field label="MT5 server" value={data.server} testid="reveal-server" />
            <Field label="MetaApi token" value={data.metaapi_token} testid="reveal-token" />
            <Field label="MetaApi account id" value={data.account_id} testid="reveal-account-id" />
            <div className="text-xs text-slate-500">{data.environment} · {data.region} · {data.verified ? "verified" : "not verified"}</div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function UserRow({ u, me, run, reveal }) {
  const b = u.broker || {};
  const self = u.id === me;
  const resetPw = () => { const p = window.prompt(`New sign-in password for ${u.email} (8+ characters)`); if (p) run(() => api.put(`/super/users/${u.id}/password`, { password: p }), "Password changed"); };
  return (
    <tr data-testid={`super-user-row-${u.email}`} className="border-b border-slate-800/60">
      <Td><div className="font-medium">{u.name}</div><div className="text-xs text-slate-500">{u.email}</div></Td>
      <Td><Chip status={STATUS[u.status]} label={u.role === "superadmin" ? "super admin" : u.role === "viewer" ? `viewer ${u.status}` : u.status} testid={`super-status-${u.email}`} /></Td>
      <Td className="text-xs">{b.configured ? <><div>{b.login_masked} · {b.server}</div><div className="text-slate-500">{b.status}</div></> : <span className="text-slate-500">Not linked</span>}</Td>
      <Td className="num" data-testid={`super-balance-${u.email}`}>{b.balance != null ? `${fmt(b.balance, 2)} ${b.currency || ""}` : "-"}</Td>
      <Td>{u.armed ? <Chip status="ARMED" label="live armed" /> : <span className="text-xs text-slate-500">Paper</span>}</Td>
      <Td className="text-xs text-slate-400">{ldnTime(u.created_at)}</Td>
      <Td>
        <div className="flex flex-wrap gap-1.5">
          {u.status === "pending" && <Button data-testid={`super-approve-${u.email}`} size="sm" className="btn-gold" onClick={() => run(() => api.post(`/super/users/${u.id}/approve`), "Approved")}>Approve</Button>}
          {b.configured && <Button data-testid={`super-reveal-${u.email}`} size="sm" variant="outline" className="bg-transparent" onClick={() => reveal(u)}><Eye className="mr-1 h-3.5 w-3.5" />Reveal</Button>}
          {!self && u.status === "active" && <Button data-testid={`super-suspend-${u.email}`} size="sm" variant="outline" className="bg-transparent text-amber-300" onClick={() => window.confirm(`Suspend ${u.email}? They cannot sign in, their engines keep running.`) && run(() => api.post(`/super/users/${u.id}/suspend`), "Suspended")}>Suspend</Button>}
          {u.status === "suspended" && <Button data-testid={`super-reactivate-${u.email}`} size="sm" variant="outline" className="bg-transparent text-emerald-300" onClick={() => run(() => api.post(`/super/users/${u.id}/reactivate`), "Reactivated")}>Reactivate</Button>}
          {u.status !== "pending" && <Button data-testid={`super-password-${u.email}`} size="sm" variant="ghost" onClick={resetPw}>Set password</Button>}
          {!self && <Button data-testid={`super-remove-${u.email}`} size="sm" variant="ghost" className="text-rose-300" onClick={() => window.confirm(u.status === "pending" ? `Decline the request from ${u.email}?` : `Remove ${u.email} and all their data? Live positions are closed first.`) && run(() => api.delete(`/super/users/${u.id}`), "Removed")}>{u.status === "pending" ? "Decline" : "Remove"}</Button>}
        </div>
      </Td>
    </tr>
  );
}

export default function SuperAdmin() {
  const [users, setUsers] = useState([]);
  const [shown, setShown] = useState(null);
  const [meId, setMeId] = useState(null);
  const [auditKey, setAuditKey] = useState(0);
  const load = async () => { setUsers((await api.get("/super/users")).data); setAuditKey((k) => k + 1); };
  useEffect(() => { load(); api.get("/auth/me").then((r) => setMeId(r.data.id)); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);
  const run = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); } };
  const reveal = async (u) => { try { setShown((await api.post(`/super/users/${u.id}/reveal`)).data); } catch (e) { toast.error(errMsg(e)); } };
  const pending = users.filter((u) => u.status === "pending");
  const others = users.filter((u) => u.status !== "pending");
  const head = <thead className="border-b border-slate-800"><tr><Th>User</Th><Th>Status</Th><Th>MT5</Th><Th>Balance</Th><Th>Live</Th><Th>Joined</Th><Th>Actions</Th></tr></thead>;
  return (
    <div className="space-y-5" data-testid="super-admin-page">
      <h1 className="font-display text-3xl font-bold tracking-tight text-slate-100">Super admin</h1>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Kpi label="Accounts" value={others.length} testid="super-kpi-accounts" />
        <Kpi label="Waiting approval" value={pending.length} testid="super-kpi-pending" />
        <Kpi label="MT5 linked" value={users.filter((u) => u.broker?.configured).length} testid="super-kpi-linked" />
        <Kpi label="Suspended" value={users.filter((u) => u.status === "suspended").length} testid="super-kpi-suspended" />
      </div>
      <Panel title={`Access requests (${pending.length})`} testid="super-pending-panel">
        {pending.length ? <div className="overflow-x-auto"><table className="w-full min-w-[760px]">{head}<tbody>{pending.map((u) => <UserRow key={u.id} u={u} me={meId} run={run} reveal={reveal} />)}</tbody></table></div>
          : <div className="text-sm text-slate-500" data-testid="super-no-pending">No requests waiting.</div>}
      </Panel>
      <NewUser onDone={load} />
      <Panel title="Accounts" testid="super-users-panel">
        <div className="overflow-x-auto"><table className="w-full min-w-[760px]">{head}<tbody>{others.map((u) => <UserRow key={u.id} u={u} me={meId} run={run} reveal={reveal} />)}</tbody></table></div>
      </Panel>
      <AuditTab key={auditKey} />
      <RevealDialog data={shown} onClose={() => setShown(null)} />
    </div>
  );
}
